/**
 * Position Validation System - Safety net to prevent units from being stuck in water
 * Runs after other systems to validate and correct unit positions
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { UnitComponent } from '@/game/components/Unit';
import { tileHeight, isLand, getSurfaceY } from '@/rendering/terrain/TerrainGenerator';

// How often to run full validation (seconds)
const VALIDATION_INTERVAL = 0.5;

export class PositionValidationSystem implements System {
  private timer: number = 0;

  update(world: World, deltaTime: number): void {
    this.timer += deltaTime;

    // Only run validation periodically to save performance
    if (this.timer < VALIDATION_INTERVAL) return;
    this.timer = 0;

    const entities = world.query([ComponentTypes.Position, ComponentTypes.Unit]);

    for (const entity of entities) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;

      // Check if unit is in water
      const height = tileHeight(position.x, position.z);
      if (!isLand(height)) {
        // Unit is in water - find nearest land and move there
        const landPos = this.findNearestLand(position.x, position.z);
        if (landPos) {
          position.x = landPos.x;
          position.z = landPos.z;
          position.y = getSurfaceY(landPos.x, landPos.z);
        }
      }
    }
  }

  /**
   * Find the nearest land position using a spiral search pattern
   */
  private findNearestLand(x: number, z: number): { x: number; z: number } | null {
    const maxRadius = 10;
    const step = 0.5;

    // Spiral search outward
    for (let radius = step; radius <= maxRadius; radius += step) {
      // Check points in a circle at this radius
      const numPoints = Math.ceil(radius * 8);
      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * Math.PI * 2;
        const checkX = x + Math.cos(angle) * radius;
        const checkZ = z + Math.sin(angle) * radius;

        if (isLand(tileHeight(checkX, checkZ))) {
          return { x: checkX, z: checkZ };
        }
      }
    }

    return null;
  }
}
